/* eslint-disable max-len */
import React, { useEffect, useState } from 'react';
import { Paper, Grid, Typography, styled, Box, Card } from '@mui/material';
import { GridItem } from '../../../../commons/styleds/styleds';
import BarChart from '../BarChart';
import DonutChart from '../DonutChart';
import GaugeChart from '../GaugeChart';
import HorizontalBarChart from '../HorizontalBarChart';
import ImgBackground from '../../../../assets/intoo_E.png';
import Logo from '../../../../assets/STATO-INTOO-LOGO-MY-INTOO-VERS-2 A.png';
import { useTranslation } from '../../../../commons/useTranslasion';
import { useDateFormatters } from '../../../../utils/useDateFormatters';

const DashboardsPDF: React.FC<any> = ({
    empresa,
    comandasPorStatus,
    comandasPorFase,
    mediaRecolocacao,
    comandasPorPlano,
    etapasFinalizadas,
    volumeIndicacoesVagas,
    colorPalette
}) => {
    const { translate } = useTranslation();
    const { formatarData } = useDateFormatters();
    const getPorcentagemStatus = (status: string) => {
        const total = comandasPorStatus.reduce((acc: any, item: any) => acc + item.quantidade, 0);
        const quantidade = comandasPorStatus.find((item: any) => item.status == status)?.quantidade;
        return (quantidade ?? 0) / total * 100;
    };

    const getPorcentagemAdesao = (expectativa: number, impactados: number, adesao = true) => {
        if (expectativa <= 0) return 0;

        const taxaAdesao = (impactados / expectativa) * 100;
        const taxaNaoAdesao = 100 - taxaAdesao;

        return Number(adesao ? taxaAdesao : taxaNaoAdesao).toFixed(0);
    }

    const [logoBase64, setLogoBase64] = useState<string>('');

    const convertImageToBase64 = async (imageUrl: string) => {
        try {
            const response = await fetch(imageUrl);
            const blob = await response.blob();
            return new Promise((resolve) => {
                const reader = new FileReader();
                reader.onloadend = () => resolve(reader.result);
                reader.readAsDataURL(blob);
            });
        } catch (error) {
            console.error(translate("errorConvertingImage"), error);
            return null;
        }
    };

    useEffect(() => {
        if (empresa?.logo) {
            convertImageToBase64(empresa.logo)
                .then((base64: any) => setLogoBase64(base64));
        }
    }, [empresa?.logo]);

    return (
        <GlobalStyle>
            <PDFContainer>
                <PDFContent>
                    <PDFHeader>
                        <HeaderContent>
                            <LogoContainer>
                                <img src={Logo} alt="logo" height="40px" />
                            </LogoContainer>
                            <TitleContainer>
                                <Typography
                                    variant="h5"
                                    sx={{
                                        mt: 1,
                                        color: '#1976d2',
                                        fontWeight: 'bold',
                                        position: 'relative',
                                        display: 'inline-block',
                                        fontSize: '1.1rem',
                                        '&::after': {
                                            content: '""',
                                            position: 'absolute',
                                            bottom: -4,
                                            left: 0,
                                            width: '100%',
                                            height: '2px',
                                            backgroundColor: '#1976d2'
                                        }
                                    }}
                                >
                                    {translate("dashboard")}
                                </Typography>
                                <Typography variant="subtitle2" sx={{ mt: 1 }}>
                                    {translate("geradoEm")} {formatarData(new Date())}
                                </Typography>
                            </TitleContainer>
                        </HeaderContent>
                    </PDFHeader>

                    <PDFSection>
                        <DivBackground>
                            <DivDashboard style={{ marginBottom: '80px' }}>
                                {empresa.logo && (
                                    <img src={logoBase64} alt="Logo" style={{ width: '250px', height: 'auto', marginLeft: '25px' }} />
                                )}

                                <Typography style={{ marginLeft: '25px' }} variant="h2">
                                    {translate("dashboardEmpresa")} {empresa.razaosocial}
                                </Typography>

                                <Box sx={{ flexGrow: 1, mt: 9 }}>
                                    <Grid
                                        container
                                        spacing={4}
                                        sx={{ mb: 6 }}
                                    >
                                        <Grid item xs={6}>
                                            <StyledCard>
                                                <Grid container spacing={3}>
                                                    <Grid item xs={12} md={6}>
                                                        <MetricBox>
                                                            <MetricValue variant="h2">
                                                                {comandasPorStatus.reduce((acc: any, item: any) => acc + item.quantidade, 0)}
                                                            </MetricValue>
                                                            <MetricLabel variant="h5">{translate("adesao")}</MetricLabel>
                                                        </MetricBox>
                                                    </Grid>

                                                    <Grid item xs={12} md={6}>
                                                        <MetricBox>
                                                            <MetricValue variant="h2">{comandasPorStatus.find((item: any) => item.status == 'Ativo')?.quantidade ?? 0}</MetricValue>
                                                            <MetricLabel variant="h5">{translate("ativos")}</MetricLabel>
                                                        </MetricBox>
                                                    </Grid>

                                                    <Grid item xs={12} md={6}>
                                                        <MetricBox>
                                                            <MetricValue variant="h2">{getPorcentagemAdesao(empresa?.estimativaAdesao ?? 0, comandasPorStatus.reduce((acc: any, item: any) => acc + item.quantidade, 0), true)}%</MetricValue>
                                                            <MetricLabel variant="h5">
                                                                de adesão (Stato)
                                                            </MetricLabel>
                                                        </MetricBox>
                                                    </Grid>

                                                    <Grid item xs={12} md={6}>
                                                        <MetricBox>
                                                            <MetricValue variant="h2">{getPorcentagemAdesao(empresa?.estimativaAdesao ?? 0, comandasPorStatus.reduce((acc: any, item: any) => acc + item.quantidade, 0), false)}%</MetricValue>
                                                            <MetricLabel variant="h5">{translate("deNaoAdesao")}</MetricLabel>
                                                        </MetricBox>
                                                    </Grid>
                                                </Grid>
                                            </StyledCard>
                                        </Grid>

                                        <Grid item xs={2}>
                                            <StyledCard>
                                                <Typography variant="h5" sx={{ textAlign: 'center', fontWeight: 'bold', color: 'black' }}>
                                                    {translate("workshopsEEventosDedicados")}
                                                </Typography>

                                                <Typography
                                                    variant="h2"
                                                    sx={{
                                                        color: '#0d47a1',
                                                        textAlign: 'center',
                                                        marginTop: '100px',
                                                        fontSize: '80px'
                                                    }}
                                                >
                                                    {empresa.eventosDedicados ?? 0}h
                                                </Typography>
                                            </StyledCard>
                                        </Grid>

                                        <Grid item xs={2}>
                                            <GaugeCard>
                                                <GaugeTitle style={{ marginTop: '25px', fontWeight: 'bold', color: 'black' }}>
                                                    {getPorcentagemStatus('Recolocado').toFixed(0)}% {translate("deRecolocacao")}
                                                </GaugeTitle>
                                                <GaugeDescription style={{ marginBottom: '45px' }}>
                                                    ({comandasPorStatus.find((item: any) => item.status == 'Recolocado')?.quantidade ?? 0} {translate("dos")} {comandasPorStatus.reduce((acc: any, item: any) => acc + item.quantidade, 0)} {translate("adesao")})
                                                </GaugeDescription>
                                                <GaugeChartContainer>
                                                    <GaugeChart
                                                        value={Math.round(getPorcentagemStatus('Recolocado'))}
                                                        color="#1976d2"
                                                    />
                                                </GaugeChartContainer>
                                            </GaugeCard>
                                        </Grid>

                                        <Grid item xs={2}>
                                            <GaugeCard>
                                                <GaugeTitle style={{ marginTop: '25px', fontWeight: 'bold', color: 'black' }}>
                                                    {mediaRecolocacao ?? 0} {translate("meses")}
                                                </GaugeTitle>
                                                <GaugeDescription>
                                                    {translate("tempoMedioPorRecolocacao")}
                                                </GaugeDescription>
                                                <GaugeChartContainer style={{ marginTop: '25px' }}>
                                                    <GaugeChart
                                                        value={mediaRecolocacao ?? 0}
                                                        color="#1976d2"
                                                        percent={false}
                                                    />
                                                </GaugeChartContainer>
                                            </GaugeCard>
                                        </Grid>
                                    </Grid>

                                    <Grid container spacing={4} sx={{ mb: 6, mt: '400px' }}>
                                        <Grid item xs={4}>
                                            <StyledCard>
                                                <Box sx={{
                                                    display: 'flex',
                                                    justifyContent: 'center',
                                                    alignItems: 'center',
                                                    position: 'relative',
                                                    width: '100%',
                                                    height: '64px',
                                                }}>
                                                    <MetricValue variant="h6" sx={{
                                                        position: 'absolute',
                                                        top: '24px',
                                                        fontWeight: 'bold',
                                                        textAlign: 'center',
                                                        width: '100%',
                                                    }}>
                                                        {translate("executivoPorFase")}
                                                    </MetricValue>
                                                </Box>
                                                <DonutChart data={comandasPorFase.sort((a: any, b: any) => a.fase.localeCompare(b.fase))} />
                                            </StyledCard>
                                        </Grid>

                                        <Grid item xs={4}>
                                            <StyledCard>
                                                <Box sx={{
                                                    display: 'flex',
                                                    justifyContent: 'center',
                                                    alignItems: 'center',
                                                    position: 'relative',
                                                    width: '100%',
                                                    height: '64px',
                                                }}>
                                                    <MetricValue variant="h6" sx={{
                                                        position: 'absolute',
                                                        top: '24px',
                                                        fontWeight: 'bold',
                                                        textAlign: 'center',
                                                        width: '100%',
                                                    }}>
                                                        {translate("programas")}
                                                    </MetricValue>
                                                </Box>
                                                <BarChart
                                                    categories={comandasPorPlano.sort((a: any, b: any) => a.plano.localeCompare(b.plano)).map((item: any) => item.plano)}
                                                    series={[{
                                                        name: 'Comandas',
                                                        data: comandasPorPlano.sort((a: any, b: any) => a.plano.localeCompare(b.plano)).map((item: any) => item.quantidade),
                                                        color: colorPalette[0]
                                                    }]}
                                                    colors={colorPalette}
                                                />
                                            </StyledCard>
                                        </Grid>

                                        <Grid item xs={4}>
                                            <StyledCard>
                                                <Box sx={{
                                                    display: 'flex',
                                                    justifyContent: 'center',
                                                    alignItems: 'center',
                                                    position: 'relative',
                                                    width: '100%',
                                                    height: '64px',
                                                }}>
                                                    <MetricValue variant="h6" sx={{
                                                        position: 'absolute',
                                                        top: '24px',
                                                        fontWeight: 'bold',
                                                        textAlign: 'center',
                                                        width: '100%',
                                                    }}>
                                                        {translate("statusDoProjeto")}
                                                    </MetricValue>
                                                </Box>
                                                <HorizontalBarChart
                                                    data={comandasPorStatus.sort((a: any, b: any) => a.status.localeCompare(b.status)).map((item: any) => ({
                                                        category: item.status,
                                                        value: item.quantidade
                                                    }))}
                                                    height={300}
                                                />
                                            </StyledCard>
                                        </Grid>
                                    </Grid>
                                </Box>
                            </DivDashboard>
                        </DivBackground>
                    </PDFSection>

                    <PDFFooter>
                        <Typography variant="body2" sx={{ color: '#666' }}>
                            © {new Date().getFullYear()} STATO - Todos os direitos reservados
                        </Typography>
                    </PDFFooter>
                </PDFContent>
            </PDFContainer>
        </GlobalStyle>
    );
};

export const LogoGridItem = styled(GridItem)`
  background: transparent;
`;

const DivBackground = styled('div')`
  background: var(--gray-background);
  min-height: 90vh;
`;

const GlobalStyle = styled('div')`
  --green2: #3cb99c;
  --green3: #5cc2aa;
  --blue1: #0288d1;
  --blue2: #1976d2;
  --blue3: #2196f3;
`;

const PDFContainer = styled('div')`
  background: white;
  width: 100%;
  height: 100%;
  min-height: 210mm;
  display: flex;
  flex-direction: column;
  padding: 5mm; // Standard print margins
`;

const PDFContent = styled('div')`
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const PDFHeader = styled('div')`
  width: 100%;
  padding: 20px;
  margin-bottom: 5px;
  background: linear-gradient(
    90deg,
    var(--green2) 0%,
    var(--green2) 20%,
    var(--green3) 40%,
    var(--green3) 60%,
    var(--blue3) 75%,
    var(--blue2) 100%
  );
  transition: width 195ms cubic-bezier(0.4, 0, 0.6, 1), margin 195ms cubic-bezier(0.4, 0, 0.6, 1);
  position: relative;

  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    height: 3px;
    background: var(--blue2);
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
  }
`;

const HeaderContent = styled('div')`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 20px;
`;

const LogoContainer = styled('div')`
  flex: 0 0 auto;
`;

const TitleContainer = styled('div')`
  flex: 1;
  
  .MuiTypography-root {
    color: white !important;
  }
`;
const PDFSection = styled('div')`
  width: 100%;
  margin: 10mm 0;

  &:first-child {
    margin-top: 0;
  }

  &:last-child {
    margin-bottom: 0;
  }

  // Adjust DataGrid container
  .MuiDataGrid-root {
    width: 100%;
    height: auto !important;
    min-height: 100mm !important;
    
    .MuiDataGrid-main {
      width: 100%;
    }
    
    .MuiDataGrid-virtualScroller {
      overflow: visible !important;
    }
  }
`;

const PDFFooter = styled('div')`
  margin-top: 40px;
  text-align: center;
  padding-top: 20px;
  border-top: 1px solid #e0e0e0;
`;

const StyledCard = styled(Card)(() => ({
    padding: '16px',
    height: '100%',
    borderRadius: '12px',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
    border: '1px solid #eeeeee', // Add border
    transition: 'transform 0.2s ease-in-out',
    position: 'relative',
    '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
    },
}));

const StyledCard2 = styled(Card)`
    position: relative;
    padding-top: 56px; 
    padding-left: 16px;
    padding-right: 16px;
    padding-bottom: 16px;
    height: 100%;
    border-radius: 12px;
    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
    border: 1px solid #eeeeee; // Add border
    transition: transform 0.2s ease-in-out;
    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 4px 8px rgba(0,0,0,0.1);
    }
  `;

const GaugeCard = styled(Card)`
    position: relative;
    padding: 16px;
    height: 100%;
    border-radius: 12px;
    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
    border: 1px solid #eeeeee; // Add border
    transition: transform 0.2s ease-in-out;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 4px 8px rgba(0,0,0,0.1);
    }
  `;

const GaugeTitle = styled(Typography)`
    font-size: 16px;
    font-weight: bold;
    color: #08467C;
    text-align: center;
  `;

const GaugeDescription = styled(Typography)`
    font-size: 14px;
    color: #757575;
    text-align: center;
    margin-bottom: 8px;
  `;

const GaugeChartContainer = styled('div')`
    display: flex;
    justify-content: center;
    align-items: center;
    flex-grow: 1;
  `;


const DivDashboard = styled('div')`
    background-image: url(${ImgBackground});
    background-repeat: no-repeat;
    background-position: top right;
    background-attachment: fixed;
    min-height: 80vh;
    padding: 12px 32px 48px;
  `;

const MetricBox = styled('div')`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: flex-start;
    text-align: left;
    padding: 16px;
    height: 100%;
    min-height: 80px;
    border-radius: 8px;
    background-color: rgba(255, 255, 255, 0.8);
    transition: transform 0.2s ease-in-out;
    gap: 12px;
    &:hover {
      transform: translateY(-2px);
      background-color: rgba(255, 255, 255, 1);
    }
  `;

const MetricValue = styled(Typography)`
    font-weight: bold;
    color: black !important;
    white-space: nowrap;
  `;

const MetricLabel = styled(Typography)`
    color: black !important;
    word-wrap: break-word;
    flex: 1;
    line-height: 1.2;
  `;

const HeaderCustom = styled('div')`
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 8px 32px 0px;
    gap: 16px;
    position: absolute;
    width: calc(100% + 2px);
    height: 40px;
    top: -1px;
    left: -1px;
    background: #F5FAFF;
    border-width: 1px 1px 0px 1px;
    border-style: solid;
    border-color: #E9EAEB;
    border-radius: 8px 8px 0px 0px;
    color: #08467C;
    font-weight: bold;
    text-align: center;
    border-bottom: 2px solid #08467C;
  `;

export default DashboardsPDF;